import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { FilterV2, AdditionalFilterV2 } from "@/components/shared/filter-v2";
import { SelectV2 } from "@/components/ui/customs/select-v2";
import { CustomsCombobox } from "@/components/ui/customs/customs-combobox";
import { DateRangePicker } from "@/components/ui/customs/date-range-picker";
import { useGetCustoms } from "@/api/customs.api";
import { PackagingFilterEnum } from "@/constants/enums/packagingFilter.enum";
import { COUNTRY_REGIONS, REGION_LABELS, type RegionCode } from "@/constants/countries";
import type { useExpeditionFilters } from "@/components/backOffice/pendingExpeditions/useExpeditionFilters.reducer";
import { usePackagingFilterOptions } from "@/hooks/useEnumOptions";

type FiltersState = ReturnType<typeof useExpeditionFilters>["filters"];
type FiltersActions = ReturnType<typeof useExpeditionFilters>["actions"];

type TriggerProps = {
  isOpen: boolean;
  onToggle: () => void;
  onReset?: () => void;
};

type PanelProps = {
  isOpen: boolean;
  filters: FiltersState;
  actions: FiltersActions;
};

export const ExpeditionsHistoryFilterTrigger = ({ isOpen, onToggle, onReset }: TriggerProps) => (
  <FilterV2 isOpen={isOpen} onToggle={onToggle} onReset={onReset} hasAdditionalFilters>
    <div className="flex flex-wrap items-center gap-2"></div>
  </FilterV2>
);

export const ExpeditionsHistoryFilterPanel = ({ isOpen, filters, actions }: PanelProps) => {
  const { t } = useTranslation("boExpeditions");
  const packagingOptions = usePackagingFilterOptions();
  const { data: customs = [] } = useGetCustoms();
  const regionOptions = useMemo(
    () =>
      (Object.keys(COUNTRY_REGIONS) as RegionCode[]).map((code) => ({
        label: REGION_LABELS[code],
        value: code,
      })),
    [],
  );

  return (
    <AdditionalFilterV2 isOpen={isOpen}>
      <div className="flex w-full flex-wrap items-center gap-2">
        <div className="w-44">
          <SelectV2
            placeholder={t("historyFilters.regionPlaceholder")}
            clearable
            options={regionOptions}
            onChange={(e) => actions.setRegion((e as RegionCode) ?? null)}
            value={filters.region ?? undefined}
            searchable={false}
          />
        </div>
        <div className="w-60">
          <CustomsCombobox
            customs={customs}
            value={filters.customId || undefined}
            onValueChange={actions.setCustomId}
            clearable
          />
        </div>
        <div className="w-52">
          <SelectV2
            placeholder={t("historyFilters.packagingPlaceholder")}
            clearable
            options={packagingOptions}
            onChange={(e) => actions.setPackagingType((e as PackagingFilterEnum) ?? null)}
            value={filters.packagingType ?? undefined}
            searchable={false}
          />
        </div>
        {/* <div className="max-w-xs min-w-[200px] flex-1">
          <CountryCombobox
            value={filters.country ?? undefined}
            onValueChange={(v) => actions.setCountry(v ?? null)}
            placeholder={t("historyFilters.countryPlaceholder")}
            clearable
            allowedCodes={filters.region ? COUNTRY_REGIONS[filters.region] : undefined}
          />
        </div> */}
        <div className="max-w-xs min-w-[200px] flex-1">
          <DateRangePicker
            placeholder={t("historyFilters.pickupDatePlaceholder")}
            value={filters.pickupDate}
            onChange={actions.setPickupDateRange}
          />
        </div>
        <div className="max-w-xs min-w-[200px] flex-1">
          <DateRangePicker
            placeholder={t("historyFilters.validatedAtPlaceholder")}
            value={filters.validatedAt}
            onChange={actions.setValidatedAtRange}
          />
        </div>

        <div className="max-w-xs min-w-[200px] flex-1">
          <DateRangePicker
            placeholder={t("historyFilters.deliveryFromDatePlaceholder")}
            value={filters.deliveryFromDate}
            onChange={actions.setDeliveryFromDateRange}
          />
        </div>
        <div className="max-w-xs min-w-[200px] flex-1">
          <DateRangePicker
            placeholder={t("historyFilters.finalizedAtPlaceholder")}
            value={filters.finalizedAt}
            onChange={actions.setFinalizedAtRange}
          />
        </div>
        <div className="max-w-xs min-w-[200px] flex-1">
          <DateRangePicker
            placeholder={t("historyFilters.deliveryDatePlaceholder")}
            value={filters.deliveryDate}
            onChange={actions.setDeliveryDateRange}
          />
        </div>
      </div>
    </AdditionalFilterV2>
  );
};
